import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { ChatModal } from '@/features/medconnect-agent/ui/ChatModal'

type CsvType = 'consultations' | 'patients' | 'medical-acts' | 'mesures' | 'pregnancies'

const CSV_SCHEMAS: Record<CsvType, string> = {
  consultations: `**consultations.csv** — One row per completed consultation.
| Column | Type | Description |
|--------|------|-------------|
| consultationId | String (ObjectId) | Unique consultation ID |
| date | DateTime ISO 8601 (e.g. 2026-01-15T09:30:00.000Z) | Start date/time |
| endDate | DateTime ISO 8601 | End date/time |
| establishmentId | String (ObjectId) | Establishment ID |
| establishmentName | String | Establishment name |
| status | Enum: always "completed" | Only completed consultations are exported |
| type | Enum: "classic", "emergency", "local", "meeting_only", "netsoins", "pedcl" | Consultation type |
| reason | String (nullable) | Reason for consultation |
| speciality | String (nullable) | Medical speciality |
| patientId | String (ObjectId) | Patient ID — **joins to patients.csv via patientId** |
| pregnancyId | String (ObjectId, nullable) | Pregnancy ID — **joins to pregnancies.csv via pregnancyId** |`,

  patients: `**patients.csv** — One row per unique patient-establishment pair (deduplicated from consultations).
| Column | Type | Description |
|--------|------|-------------|
| patientId | String (ObjectId) | Unique patient ID — **joins to consultations.csv, mesures.csv, medical-acts.csv, pregnancies.csv** |
| birthday | Date (YYYY-MM-DD, e.g. 1990-05-21) | Date of birth — **use this to calculate age** |
| gender | Enum: "M" (Male), "F" (Female), "I" (Unknown) | Patient gender |
| establishmentId | String (ObjectId) | Establishment ID |
| establishmentName | String | Establishment name |`,

  'medical-acts': `**medical-acts.csv** — One row per medical act performed.
| Column | Type | Description |
|--------|------|-------------|
| actId | String (ObjectId) | Unique act ID |
| templateCode | String (nullable) | Act template code |
| templateName | String (nullable) | Act template name (e.g. "Consultation générale", "Échographie") |
| price | Number (nullable) | Price at time of act |
| performedAt | DateTime ISO 8601 | When the act was performed |
| patientId | String (ObjectId) | Patient ID — **joins to patients.csv** |
| consultationId | String (ObjectId, nullable) | Consultation ID — **joins to consultations.csv** |
| expertiseId | String (ObjectId, nullable) | Expertise/referral ID |
| establishmentId | String (ObjectId) | Establishment ID |
| establishmentName | String | Establishment name |`,

  mesures: `**mesures.csv** — One row per measurement taken during a consultation. Only rows with non-null values are exported.
| Column | Type | Description |
|--------|------|-------------|
| consultationId | String (ObjectId) | Consultation ID — **joins to consultations.csv** |
| patientId | String (ObjectId, nullable) | Patient ID — **joins to patients.csv** |
| establishmentId | String (ObjectId) | Establishment ID |
| establishmentName | String | Establishment name |
| date | DateTime ISO 8601 | Consultation start date |
| label | String | Measurement type. Common values: "Tension" (mmHg), "Saturation O2" (%), "Pouls Périphériques" (bpm), "Température" (°C), "Poids" (kg), "Taille" (cm) |
| value | String | Measured value (as text, may need numeric conversion) |
| unit | String | Unit of measurement |`,

  pregnancies: `**pregnancies.csv** — One row per pregnancy.
| Column | Type | Description |
|--------|------|-------------|
| pregnancyId | String (ObjectId) | Unique pregnancy ID — **joins to consultations.csv via pregnancyId** |
| patientId | String (ObjectId) | Patient ID — **joins to patients.csv** |
| lmpDate | Date (YYYY-MM-DD) | Last Menstrual Period date — **use to calculate gestational age and trimester**: T1 = weeks 0-13, T2 = weeks 14-27, T3 = weeks 28+ |
| expectedDueDate | Date (YYYY-MM-DD) | Expected due date (typically lmpDate + 280 days) |
| status | Enum: "active" (ongoing), "delivered" (birth completed), "ended" (pregnancy ended) | Pregnancy status |
| establishmentId | String (ObjectId) | Establishment ID |
| establishmentName | String | Establishment name |`,
}

function buildInitialPrompt(selectedTypes: CsvType[], description: string): string {
  const schemasSection = selectedTypes.map((type) => CSV_SCHEMAS[type]).join('\n\n')

  return `I need you to generate a VBA macro for Microsoft Excel that creates a dashboard from CSV data files exported from MedConnect (a telemedicine platform).

## CSV Data Schemas

${schemasSection}

## Key Relationships Between CSVs
- **patientId** is the primary join key across all CSVs (link patients to their consultations, measurements, acts, and pregnancies)
- **consultationId** links consultations to their measurements and medical acts
- **pregnancyId** links consultations to pregnancies
- **establishmentId/establishmentName** is present in all CSVs for filtering by health facility
- A patient can appear in multiple establishments

## How to Compute Common Metrics
- **Patient age**: Calculate from patients.csv \`birthday\` column (YYYY-MM-DD format) relative to today. Age groups: 0-14, 15-24, 25-34, 35-44, 45-59, 60+
- **Gender breakdown**: Use patients.csv \`gender\` column (M/F/I)
- **Pregnancy trimester**: Calculate weeks since \`lmpDate\`: T1 = 0-13 weeks, T2 = 14-27 weeks, T3 = 28+ weeks
- **Consultation duration**: Difference between \`endDate\` and \`date\` columns
- **Measurement values**: The \`value\` column in mesures.csv is text — cast to numeric (CDbl) for calculations
- **Patient frequency/count**: Count distinct patientId values in consultations.csv (a patient may have multiple consultations)

## Dashboard Description
${description}

## Requirements
- Generate a complete, ready-to-use VBA Sub procedure with ALL calculation logic implemented (no placeholder zeros — compute real values from the data)
- Import CSVs using Workbooks.Open (assume CSV files are in the same folder as the Excel file)
- Parse dates properly: use CDate() on ISO 8601 datetime strings, and DateValue() on YYYY-MM-DD date strings
- Implement all aggregations (counting, grouping, filtering) by iterating through the loaded data rows
- Add formatting (headers, borders, number formats) to make the dashboard presentable
- If charts are appropriate for the described dashboard, include chart creation code
- Include basic error handling (On Error Resume Next around file operations)

Please generate the complete VBA macro code now.`
}

export function VbaMacroGenerator() {
  const { t } = useTranslation()

  const [selectedTypes, setSelectedTypes] = useState<Record<CsvType, boolean>>({
    consultations: false,
    patients: false,
    'medical-acts': false,
    mesures: false,
    pregnancies: false,
  })
  const [description, setDescription] = useState('')
  const [chatOpen, setChatOpen] = useState(false)
  const [initialPrompt, setInitialPrompt] = useState<string | undefined>(undefined)
  const [validationError, setValidationError] = useState<string | null>(null)

  const csvTypeLabels: Record<CsvType, string> = {
    consultations: t('dataExport.consultations', { defaultValue: 'Consultations' }),
    patients: t('dataExport.patients', { defaultValue: 'Patients' }),
    'medical-acts': t('dataExport.medicalActs', { defaultValue: 'Medical Acts' }),
    mesures: t('dataExport.mesures', { defaultValue: 'Measures' }),
    pregnancies: t('dataExport.pregnancies', { defaultValue: 'Pregnancies' }),
  }

  const handleToggleCsvType = (type: CsvType) => {
    setSelectedTypes((prev) => ({ ...prev, [type]: !prev[type] }))
    setValidationError(null)
  }

  const handleGenerate = () => {
    const selected = (Object.entries(selectedTypes) as [CsvType, boolean][])
      .filter(([, v]) => v)
      .map(([k]) => k)

    if (selected.length === 0) {
      setValidationError(t('dataExport.vba.error.selectCsvType', { defaultValue: 'Please select at least one CSV type.' }))
      return
    }
    if (!description.trim()) {
      setValidationError(t('dataExport.vba.error.descriptionRequired', { defaultValue: 'Please describe the dashboard you want to create.' }))
      return
    }

    setValidationError(null)
    setInitialPrompt(buildInitialPrompt(selected, description.trim()))
    setChatOpen(true)
  }

  const handleChatClose = () => {
    setChatOpen(false)
    setInitialPrompt(undefined)
  }

  return (
    <>
      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoFixHighIcon color="primary" />
            <Typography variant="h6">
              {t('dataExport.vba.title', { defaultValue: 'VBA Macro Generator' })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('dataExport.vba.instructions', {
              defaultValue:
                'Select the CSV types your dashboard will use, describe what you want, and let Elara generate a VBA macro for you. You can iterate on the result conversationally.',
            })}
          </Alert>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('dataExport.vba.selectCsvTypes', { defaultValue: 'Select CSV types:' })}
          </Typography>
          <FormGroup row sx={{ mb: 2 }}>
            {(Object.keys(CSV_SCHEMAS) as CsvType[]).map((type) => (
              <FormControlLabel
                key={type}
                control={<Checkbox checked={selectedTypes[type]} onChange={() => handleToggleCsvType(type)} />}
                label={csvTypeLabels[type]}
              />
            ))}
          </FormGroup>

          <TextField
            label={t('dataExport.vba.descriptionLabel', { defaultValue: 'Dashboard description' })}
            placeholder={t('dataExport.vba.descriptionPlaceholder', {
              defaultValue: 'e.g. A monthly summary with consultation counts by speciality, a bar chart, and patient demographics breakdown',
            })}
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            value={description}
            onChange={(e) => {
              setDescription(e.target.value)
              setValidationError(null)
            }}
            sx={{ mb: 2 }}
          />

          {validationError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <Button variant="contained" startIcon={<AutoFixHighIcon />} onClick={handleGenerate}>
            {t('dataExport.vba.generateButton', { defaultValue: 'Generate with Elara' })}
          </Button>
        </AccordionDetails>
      </Accordion>

      <ChatModal
        open={chatOpen}
        onClose={handleChatClose}
        sessionId={null}
        initialPrompt={initialPrompt}
      />
    </>
  )
}

export default VbaMacroGenerator
